import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';

import Link from 'shared/components/Link';
import { generateUrl } from 'shared/helpers/urls';
import { carPrice, carPriceBySlug } from 'shared/helpers/transportInfo';
import { numberWithSpaces } from 'shared/helpers/common';
import { isVIP } from 'shared/helpers/advert';

import currencies from 'shared/constants/json/currency.json';
import mileageUnits from 'shared/constants/json/mileageUnits.json';
import { getDefaultImageByType } from 'shared/constants/defaultImages';

import AdvertLabel from 'shared/components/AdvertLabel/AdvertLabel';

import SimpleCard from './SimpleLeasingCard/SimpleLeasingCard';
import SimilarCard from './SimilarLeasingCard/SimilarLeasingCard';
import DealerAdsCard from './DealerAdsLeasingCard/DealerAdsLeasingCard';
import PromoutedCard from './PromoutedLeasingCard/PromoutedLeasingCard';

import Thumb from '../Thumb/Thumb';
// eslint-disable-next-line css-modules/no-unused-class
import s from './AdvertCarCard.css';

const UAH_CURRENCY = currencies.find(currency => currency.slug === 'uah');
const USD_CURRENCY = currencies.find(currency => currency.slug === 'usd');
const EUR_CURRENCY = currencies.find(currency => currency.slug === 'eur');

class AdvertCarCard extends React.Component {
  static propTypes = {
    advert: PropTypes.shape().isRequired,
    // not required:
    advertStyle: PropTypes.shape(),
    imageStyle: PropTypes.shape(),
    currency: PropTypes.shape(),
    advertClassName: PropTypes.string,
    advertThumbClassName: PropTypes.string,
    cardType: PropTypes.string,
    width: PropTypes.number,
    link: PropTypes.string,
    defaultImage: PropTypes.string,
    lensEvent: PropTypes.string,
    index: PropTypes.number,
    //
    leasingParams: PropTypes.shape(),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    advertStyle: {},
    imageStyle: {},
    currency: UAH_CURRENCY,
    lensEvent: '',
    advertClassName: '',
    advertThumbClassName: '',
    cardType: '',
    width: 240,
    link: null,
    leasingParams: null,
    defaultImage: null,
    index: 0,
  };

  buildCarPrice = (prices, currency) => {
    if (!Array.isArray(prices)) {
      return null;
    }

    const priceObj = prices.find(price => price.currency === currency.slug);
    return (
      <span>
        {priceObj && numberWithSpaces(priceObj.value)} <small>{currency && currency.title}</small>
      </span>
    );
  };

  render() {
    const {
      advertClassName,
      advertThumbClassName,
      advertStyle,
      imageStyle,
      currency,
      advert,
      cardType,
      width,
      advert: {
        maker = '',
        make = '',
        model = '',
        year = '',
        mileage = '',
        // mileage_units: mUnit = '',
        mileage_unit: mileageUnitTempId = 0,
        engine = '',
        gearbox = '',
        price = '',
        characteristics = '',
        transport,
        color_date: colorDate,
        is_new: isNew,
        clarify_price_link: clarifyPriceLink,
        is_negotiable_price: isNegotiablePrice,
      },
      link: linkFromProps,
      intl,
      lensEvent,
      index,
    } = this.props;

    const link = linkFromProps || `/${generateUrl(advert)}`;
    const fullPrice = clarifyPriceLink
      ? null
      : this.buildCarPrice(price, currency) || carPrice(price, currency.slug);

    const mileageUnitId = mileageUnitTempId || 0;
    const engineTitle = engine
      ? engine.title
      : characteristics && characteristics.engine && characteristics.engine.title;
    const gearboxTitle = gearbox
      ? gearbox.title
      : characteristics && characteristics.gearbox && characteristics.gearbox.title;

    const mileageUnit = mileageUnits.find(unit => unit.id === mileageUnitId);

    const defaultImage = this.props.defaultImage || getDefaultImageByType(transport);
    const shouldEmphasizePrice = colorDate ? new Date(colorDate) > new Date() : false;

    const mileageUnitTitle =
      (mileageUnit && mileageUnit.title && intl.formatMessage({ id: mileageUnit.title })) || '';

    let mileageText = mileage > 0 ? `${mileage} ${mileageUnitTitle}` : null;

    const priceUAH = carPriceBySlug(price, UAH_CURRENCY);
    const priceUSD = carPriceBySlug(price, USD_CURRENCY);
    const priceEUR = carPriceBySlug(price, EUR_CURRENCY);

    if (isNew) {
      mileageText = intl.formatMessage({ id: 'Новый' });
    }

    const sharedProps = {
      advert,
      maker,
      make,
      model,
      year,
      fullPrice,
      shouldEmphasizePrice,
      isNegotiablePrice,
      intl,
      priceUAH,
      priceUSD,
      priceEUR,
    };

    const renderSwitchParams = () => {
      switch (cardType && cardType.toLowerCase()) {
        case 'similar': {
          return <SimilarCard {...sharedProps} index={index} />;
        }
        case 'dealerads': {
          return <DealerAdsCard {...sharedProps} />;
        }
        case 'simple': {
          return (
            <SimpleCard
              {...sharedProps}
              mileageText={mileageText}
              engineTitle={engineTitle || ''}
              gearboxTitle={gearboxTitle || ''}
            />
          );
        }
        case 'brick': {
          return <SimpleCard make={make} model={model} />;
        }
        default: {
          return (
            <PromoutedCard
              {...sharedProps}
              index={index}
              mileageText={mileageText}
              engineTitle={engineTitle || ''}
              gearboxTitle={gearboxTitle || ''}
            />
          );
        }
      }
    };
    const carTypeStyle = cardType.toLowerCase() || 'promoutedcard';
    return (
      <div className={s.root} style={advertStyle}>
        <div
          className={cx(advertClassName, {
            [s[carTypeStyle]]: !!s[carTypeStyle],
            [s.isVip]: isVIP(advert),
          })}
        >
          <div className={s.thumb}>
            <Thumb
              key={Math.random()}
              photos={advert.photos}
              link={link}
              style={imageStyle}
              className={advertThumbClassName}
              width={width}
              defaultImg={defaultImage}
              lensEvent={lensEvent}
            />
          </div>
          <div
            className={cx(s.subInfo, { [s.brick]: cardType && cardType.toLowerCase() === 'brick' })}
          >
            {renderSwitchParams()}
          </div>
          <AdvertLabel advert={advert} />
          {link && <Link to={link} className={s.link} data-lens-event={lensEvent} />}
        </div>
      </div>
    );
  }
}

export default withStyles(s)(injectIntl(AdvertCarCard));



// WEBPACK FOOTER //
// ./src/desktop/components/AdvertCarCard/AdvertCarCard.js